<template>
  <div class="p-5 my-4">
    <div class="grid gap-4 md:grid-cols-2 md:gap-0 lg:grid-cols-4">
      <div class="border-b pb-3 md:border-r lg:border-b-0 lg:pb-0">
        <div class="text-center">
          <span class="font-bold text-xl">Status</span>
        </div>
        <div>
          <ChartPercentGauge
            height="150"
            :value="[calculateProfitMargin]"
            :options="chartOptions"
          />
        </div>
      </div>

      <div class="border-b pb-3 lg:border-b-0 lg:pb-0 lg:border-r">
        <div class="mb-6 flex items-center justify-center text-xl font-bold gap-x-1">
          <span class="i-heroicons-chart-bar-solid " />
          <span>Total</span>
        </div>

        <div class="grid grid-cols-2 justify-items-center">
          <div class="text-slate-500 text-sm font-bold">
            Actual
          </div>
          <div>
            {{ currency(actualTotal) }}
          </div>
        </div>

        <div class="grid grid-cols-2 justify-items-center">
          <div class="text-slate-500 text-sm font-bold">
            Budget
          </div>

          <div>
            {{ currency(totalBudgetExpenses) }}
          </div>
        </div>
      </div>

      <div class="border-b pb-3 md:border-r md:border-b-0 md:pb-0 md:pt-3 lg:pt-0">
        <div class="mb-6 flex items-center justify-center text-xl font-bold gap-x-1">
          <span class="i-heroicons-cube"  />
          <span>Materials</span>
        </div>

        <div class="grid grid-cols-2 justify-items-center">
          <div class="text-slate-500 text-sm font-bold">
            Actual
          </div>
          <div>
            {{ currency(actualMaterialCost) }}
          </div>
        </div>

        <div class="grid grid-cols-2 justify-items-center">
          <div class="text-slate-500 text-sm font-bold">
            Budget
          </div>
          <div>
            {{ currency(materialsBudget) }}
          </div>
        </div>
      </div>

      <div class="md:pt-3 lg:pt-0">
        <div class="mb-6 flex items-center justify-center text-xl font-bold gap-x-1">
          <span class="i-heroicons-clock-solid"  />
          <span>Labor</span>
        </div>

        <div class="grid grid-cols-2 justify-items-center">
          <div class="text-slate-500 text-sm font-bold">
            Actual
          </div>
          <div>
            {{ currency(actualLaborCost) }}
          </div>
        </div>

        <div class="grid grid-cols-2 justify-items-center">
          <div class="text-slate-500 text-sm font-bold">
            Budget
          </div>
          <div>
            {{ currency(laborsBudget) }}
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import ChartPercentGauge from '@/components/ChartPercentGauge.vue'
import { toNumber } from '@/utils'
import { computed, ref } from 'vue'
import { currency } from '@/utils'


const props = defineProps({
  materialsBudget: {
    type: Number,
    default: 0
  },

  actualMaterialCost: {
    type: Number,
    default: 0
  },

  actualLaborCost: {
    type: Number,
    default: 0
  },

  laborsBudget: {
    type: Number,
    default: 0
  },

  totalBudgetExpenses: {
    type: Number,
    default: 0
  },

  totalBudgetRevenue: {
    type: Number,
    default: 0
  }
})

const chartOptions = ref({
  plotOptions: {
    radialBar: {
      hollow: {
        size: '70%'
      },
      dataLabels: {
        name: {
          fontSize: '12px',
          offsetY: 25
        },
        value: {
          show: true,
          fontSize: '20px',
          fontWeight: '100',
          offsetY: -12
        }
      }
    }
  }
})

const calculateProfit = computed(() => props.totalBudgetRevenue - props.totalBudgetExpenses)

const  actualTotal = computed(() => {
  if (isNaN(props.actualMaterialCost + props.actualLaborCost)) {
    return 0
  } else {
    return toNumber(props.actualMaterialCost) + toNumber(props.actualLaborCost)
  }
})


const calculateProfitMargin = computed(() => {
  if (calculateProfit.value) {
    if (toNumber(calculateProfit.value) <= 0) {
      return 0
    } else {
      const percent = ((toNumber(calculateProfit.value) / toNumber(props.totalBudgetRevenue)) * 100)
      return isNaN(percent) ? 0 : percent.toFixed(2)
    }
  } else {
    return 0
  }
})

</script>
